// Multiplexed per-account live view (#83): consume the API's one-connection SSE
// feed (`GET /v1/streams?topics=…`) and PATCH ROWS IN PLACE. The account-wide
// generalization of `lib/fleetStream.ts` (one run's append-only events): here a
// single connection fans in several topics, and each `patch` frame upserts (or
// removes) ONE row keyed by id. A Solid store makes that O(1) — a busy fleet wall
// re-renders only the row that moved, never the whole list, and never re-fetches.
//
// This module owns ONE CONNECTION. Who subscribes which topics over it is
// `lib/stream-topics.ts`, so a page holds one socket rather than one per widget.
//
// The socket, the framing and the reconnect ladder are `lib/sse-subscribe.ts` —
// shared with the two append-log views, which dial the same way (`fetch` + a
// stream reader, so the dev `dev_pat_` bearer rides along where native
// EventSource could not). What is NOT shared is the RECONNECT POSTURE: an
// append log stops on a bare close and offers a Retry, while this one is
// long-lived and reconnects on every close, because the server recycles the
// connection at its max-duration cap by design. That is the `always` policy,
// passed here as a value — the next snapshot re-patches the store, and patches
// are idempotent, so a replay costs nothing.

import { batch, createEffect, createSignal, onCleanup, untrack } from 'solid-js';
import { createStore, type SetStoreFunction } from 'solid-js/store';
import { applyAccountFrames } from './account-stream-frames';
import {
  applyPatch,
  listTopic,
  resetStore,
  type StreamStore,
  seedTopics,
} from './account-stream-store';
import { API_BASE_URL } from './api';
import {
  type ChangeFeed,
  type ChangeFeedSource,
  createChangeFeed,
  type StreamSignal,
} from './live/change-feed';
import { armRedial, recoveryFor } from './live/redial';
import { createVersionMemory, type VersionMemory } from './live/version-memory';
import { MOCKS } from './mocks';
import { urlOrg } from './org';
import { type SseOutcome, sseSubscribe } from './sse-subscribe';
import { DEFAULT_SSE_RETRY_MS } from './sseBackoff';
import type { SseFrame } from './sseFrames';

export type AccountStreamStatus = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'error';

export type AccountStream = {
  readonly store: StreamStore;
  readonly status: () => AccountStreamStatus;
  /**
   * The topics the SERVER said it is pumping (`ready.subscribed`), or `null`
   * before a connection has announced itself. A topic the token cannot read is
   * absent here and never patches — see `stream-topics.ts` `statusForTopics`.
   */
  readonly served: () => ReadonlySet<string> | null;
  readonly list: (topic: string) => unknown[];
  /**
   * What moved, by `(topic, key)`, plus each connection's `ready` — the signal a
   * REST read re-reads on (`lib/live/live-query.ts`). Identities only, never a row.
   */
  readonly changes: ChangeFeed;
};

// The patch frame's type is the SHARED wire contract (`@developerz/domain`
// `wire/sse-frames.ts`), re-exported so this app's tests keep importing it from
// their own client. The queue-standing key and the schema re-exports went with
// their last consumers; `account-stream-frames.ts` still guards that key at the
// door, because the server still sends it on `tasks`.
export type { StreamPatch } from '@developerz/domain';

/**
 * The status a STOPPED loop leaves behind, ENUMERATED rather than asserted.
 *
 * Under `always` there are THREE non-abort exits: `shouldReconnect` answers
 * true for exactly `recycled | dropped | unavailable` (`sse-subscribe.test.ts`
 * pins that list), so `refused`, `ended` and `crashed` (a consumer's own throw,
 * already reported) all come back here. The branch that stood here handled
 * `refused` and called it the only one, so a `done` frame set no status and
 * armed no dial — a green `live` pill over a socket nobody was reading, for the
 * life of the tab.
 *
 * A `Record` over the union rather than an `if`, so a new `SseOutcome` kind is
 * a compile error here and not another silent freeze. `ended` fails LOUD:
 * a server that ends a feed with no terminal state ends the next one too, so
 * `error` — which paints the fault and keeps the REST backfill polling
 * (`statusForTopics`) — beats a re-dial that changes nothing. The three
 * reconnecting kinds cannot arrive; reaching one means the loop stopped without
 * dialling, which is not live either. Only `aborted` leaves the status alone —
 * an unmount, an org switch or a topic change is already tearing this down.
 *
 * `error` is no longer for the life of the tab: a stopped stream arms ONE
 * re-dial (`live/redial.ts` — `online`, the tab turning visible, or a timer at
 * the ladder's cap), except after a 401, whose answer is sign-in.
 */
/**
 * How long a DOWN stream's rows stay the truth: one REST poll (the 30 s
 * `LIVE_FALLBACK_POLL_MS` every live read resumes once the stream does not vouch,
 * `lib/live/live-query.ts`). A topic-reading view (`mergeFleetLive`) trusts a
 * PRESENT topic over the REST answer, so rows held past this hid every poll answer behind the pre-drop
 * snapshot, live Approve / Skip and all (#5608 slice 17 row 3). A routine recycle
 * re-dials well inside it, so the board does not flash back to the backfill.
 */
export const STREAM_STALE_MS = 30_000;

const STATUS_AFTER: Record<SseOutcome['kind'], AccountStreamStatus | null> = {
  aborted: null,
  ended: 'error',
  refused: 'error',
  recycled: 'error',
  dropped: 'error',
  unavailable: 'error',
  crashed: 'error',
};

/** One `/v1/streams` connection: the dev bearer and the URL's org (#460), as `apiFetch` sends them. */
function dialStreams(
  query: string,
  token: string,
  org: string | null,
  signal: AbortSignal,
): Promise<Response> {
  const bearer = token.startsWith('dev_pat_') ? { authorization: `Bearer ${token}` } : {};
  const orgHeader = org !== null ? { 'x-dz-account': org } : {};
  return fetch(`${API_BASE_URL}/v1/streams?topics=${encodeURIComponent(query)}`, {
    credentials: 'include',
    headers: { accept: 'text/event-stream', ...bearer, ...orgHeader },
    signal,
  });
}

type ChunkSinks = {
  readonly setStore: SetStoreFunction<StreamStore>;
  readonly setServed: (served: ReadonlySet<string> | null) => void;
  readonly setStatus: (status: AccountStreamStatus) => void;
  readonly clearStale: () => void;
  readonly feed: ChangeFeedSource;
  /** When this connection's dial started — its `ready`'s `since` (see `StreamSignal`). */
  readonly dialedAt: () => number;
  /** Which patches are news, across connections (`live/version-memory.ts`). */
  readonly versions: VersionMemory;
  /**
   * The topics the previous connection served live until OUR abort, consumed by
   * this connection's first `ready` (its `continued`); null when there was a gap.
   */
  readonly takeCarried: () => ReadonlySet<string> | null;
};

/**
 * One network chunk: the rows into the store in ONE batch, then its changes to
 * the feed — after the batch, so a listener reading the store sees the chunk.
 */
function applyChunk(frames: readonly SseFrame[], reset: boolean, sinks: ChunkSinks): void {
  const { setStore, setServed, setStatus, clearStale, feed, dialedAt, versions, takeCarried } =
    sinks;
  const signals: StreamSignal[] = [];
  batch(() => {
    // The reset lands with the connection's first frames — its opening `ready`
    // among them, whose `seedTopics` re-presents every served topic — in ONE
    // batch, so no render sees a topic missing.
    if (reset) {
      resetStore(setStore);
      setServed(null);
    }
    applyAccountFrames(frames, {
      // `live` is the SERVER's answer, not `res.ok`. A token that lost a topic's
      // scope still gets a 200 and a connection; what it does not get is that
      // topic in `subscribed`, and flipping the pill green on the response alone
      // painted "live" over a board that could never fill (`partitionTopics`).
      //
      // It is also where each served topic becomes PRESENT in the store, empty:
      // an empty snapshot sends no patch, and an absent topic reads as "not
      // streamed yet" to a topic-reading view — see `seedTopics`.
      onReady: (ready) => {
        clearStale();
        seedTopics(setStore, ready.subscribed);
        setServed(new Set(ready.subscribed));
        setStatus('live');
        const resumed = versions.rotate(ready.subscribed);
        const carried = takeCarried();
        const continued = carried === null ? [] : ready.subscribed.filter((t) => carried.has(t));
        signals.push({
          kind: 'ready',
          subscribed: ready.subscribed,
          since: dialedAt(),
          resumed,
          continued,
        });
      },
      onPatch: (patch) => applyPatch(setStore, patch),
      // A replayed row at the version this tab already saw is not a change.
      onChange: (patch) => {
        if (versions.isNews(patch)) {
          signals.push({ kind: 'changed', topic: patch.topic, key: patch.key });
        }
      },
    });
  });
  feed.emit(signals);
}

/** The outage clock — see `STREAM_STALE_MS`. Armed once per outage; `clear` disarms it. */
function createOutageClock(): {
  readonly arm: (onStale: () => void) => void;
  readonly clear: () => void;
} {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return {
    arm: (onStale) => {
      timer ??= setTimeout(onStale, STREAM_STALE_MS);
    },
    clear: () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    },
  };
}

/**
 * The topics a connection carries over WITHOUT a gap: the ones the previous
 * connection served live up to the instant OUR abort closed it, when the SAME
 * effect re-run dials the next one (a topic-set change, dial to dial). They
 * become that connection's first `ready`'s `continued` (`StreamSignal`). Nothing
 * carries across an idle dwell (no topics: the dial branch resets it), a close
 * before that `ready` (a real gap), or an account switch.
 */
function createCarry(): {
  /** The dial run is being torn down: what it served, if it was live. */
  readonly aborted: (liveTopics: ReadonlySet<string> | null) => void;
  /** A new dial run starts under `org`; true when that is an account switch (nothing carries). */
  readonly begin: (org: string | null) => boolean;
  /** This run's first `ready` takes the carry; every later one gets null. */
  readonly take: () => ReadonlySet<string> | null;
  /** The connection closed before its `ready`: a gap, so nothing carries. */
  readonly drop: () => void;
} {
  let fromAbort: ReadonlySet<string> | null = null;
  let pending: ReadonlySet<string> | null = null;
  let lastOrg: string | null | undefined;
  return {
    aborted: (liveTopics) => {
      fromAbort = liveTopics;
    },
    begin: (org) => {
      const switched = org !== lastOrg;
      lastOrg = org;
      pending = switched ? null : fromAbort;
      fromAbort = null;
      return switched;
    },
    take: () => {
      const c = pending;
      pending = null;
      return c;
    },
    drop: () => {
      pending = null;
    },
  };
}

/**
 * Open the multiplexed stream for `topics` and expose the patch-in-place `store`,
 * a connection status and the negotiated topic set. `topics` is a reactive
 * accessor: a change tears the old stream down and opens a fresh one (resetting
 * the store). Aborts on cleanup.
 */
export function useAccountStream(
  topics: () => readonly string[],
  token: string | null,
  opts: { reconnectMs?: number; random?: () => number } = {},
): AccountStream {
  // Starting pace only, and the BASE of the ladder rather than the whole of it.
  // The server publishes its own `retry:` on a connection's first frame
  // (`sseWriter`, `apps/api/src/middleware/sse-stream.ts`) and that wins from
  // then on — it is the operator's one lever over how hard a fleet of open tabs
  // re-dials while the api rolls. `sseBackoff.ts` says why a constant is not
  // enough on its own.
  const startingRetryMs = opts.reconnectMs ?? DEFAULT_SSE_RETRY_MS;
  const random = opts.random ?? Math.random;
  const [store, setStore] = createStore<StreamStore>({});
  const [status, setStatus] = createSignal<AccountStreamStatus>('connecting');
  const [served, setServed] = createSignal<ReadonlySet<string> | null>(null);
  // Bumped by a stopped stream's recovery (`live/redial.ts`) to re-run the dial.
  const [redials, setRedials] = createSignal(0);
  const feed = createChangeFeed();
  // Hub-lived, ACROSS connections — that is its point; cleared on an org switch.
  const versions = createVersionMemory();
  const carry = createCarry();
  const view: AccountStream = {
    store,
    status,
    served,
    list: (topic) => listTopic(store, topic),
    changes: { listen: feed.listen },
  };

  // No credential (tests / pre-SSO) or mock mode → no real API to stream from.
  if (token === null || MOCKS) {
    setStatus('idle');
    return view;
  }

  createEffect(() => {
    redials();
    const query = topics().join(',');
    // Nobody is subscribed. `?topics=` empty is a 400 (`topicsQuerySchema`
    // refuses an empty list), so dialling would burn a request to be told so and
    // then park the shell's stream in a terminal `error` for the rest of the
    // session. The hub's registry legitimately empties — a route with no live
    // view mounted under a shell whose bell is closed.
    if (query === '') {
      // Idle watches nothing: the connection after this dwell carries no topic over.
      carry.aborted(null);
      setStatus('idle');
      setServed(null);
      return;
    }
    // Track `urlOrg()` in this scope (#460): an org switch that keeps the same
    // topic set must still tear the old stream down and reopen under the new
    // account. Reset for the new topic set; onCleanup below aborts the previous.
    const org = urlOrg();
    if (carry.begin(org)) versions.clear();
    resetStore(setStore);
    setServed(null);
    setStatus('connecting');
    const controller = new AbortController();
    /** A stopped stream's armed recovery; the cleanup below always disarms it. */
    let disarmRedial: (() => void) | undefined;
    // Set by `onOpen`, consumed by that connection's first `onFrames`.
    let resetPending = false;
    // When the CURRENT attempt's dial started: the `since` its `ready` reports.
    let dialAt = Date.now();
    const stale = createOutageClock();
    const dropRows = (): void => {
      stale.clear();
      batch(() => {
        resetStore(setStore);
        setServed(null);
      });
    };
    void (async () => {
      const outcome = await sseSubscribe({
        // Every close reconnects: a recycle at the duration cap is ROUTINE here,
        // and so is an api pod rolling under a tab nobody is touching.
        policy: { kind: 'always', random },
        signal: controller.signal,
        startingRetryMs,
        dial: () => {
          dialAt = Date.now();
          return dialStreams(query, token, org, controller.signal);
        },
        // Resync from scratch on EVERY connection — see `resetStore` — but not
        // HERE: `onOpen` and the connection's first frames are separate reader
        // ticks, and a store reset in between leaves every topic ABSENT for a
        // render, which a topic-reading view reads as "not streamed yet" and paints the
        // REST backfill (page-load old; its poll is off while live). Until the
        // new connection speaks, the previous connection's rows stand.
        onOpen: () => {
          resetPending = true;
        },
        onFrames: (frames) => {
          const reset = resetPending;
          resetPending = false;
          applyChunk(frames, reset, {
            setStore,
            setServed,
            setStatus,
            clearStale: stale.clear,
            feed,
            dialedAt: () => dialAt,
            versions,
            takeCarried: carry.take,
          });
        },
        onReconnect: (why) => {
          carry.drop();
          setStatus('reconnecting');
          // Armed once per outage, cleared by the next `ready` (`onReady` below).
          // Not on an announced recycle: that server is healthy and the re-dial is
          // routine; if that re-dial then fails, its own outcome arms the clock.
          if (why.kind !== 'recycled') stale.arm(dropRows);
        },
      });
      const stopped = STATUS_AFTER[outcome.kind];
      if (stopped === null) return;
      setStatus(stopped);
      // Stopped: nothing will patch these rows again, so they are not the
      // truth — the topics go absent and a topic-reading view renders the REST poll.
      dropRows();
      // …until the recovery re-dials (`live/redial.ts` says when, and why a 401
      // never does). The re-dial re-runs this effect, whose cleanup disarms.
      const recovery = recoveryFor(outcome, random);
      if (recovery !== null && !controller.signal.aborted) {
        disarmRedial = armRedial(() => setRedials((n) => n + 1), recovery.timerMs);
      }
    })();

    onCleanup(() => {
      carry.aborted(untrack(status) === 'live' ? untrack(served) : null);
      controller.abort();
      stale.clear();
      disarmRedial?.();
    });
  });

  return view;
}
